// Angular
import { Component, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
// Afidium
import { ILabels } from "libraries/interfaces/ILabels";
import { Yii } from "libraries/interfaces/Yii";
import { IMenuDetails } from "libraries/hotel/models/menu-details";
import { MenuDetailsService } from "libraries/hotel/components/menu-details/services/menu-details.service";
import {
    IGetBookingsAirResponse,
    IGetBookingsStayResponse, IGetBookingsVehicleRentalResponse,
    ISectorAir,
    ISectorStay,
    ITicket,
    ITraveller,
    IXId,
    TGetBookingResponse
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import { IPageChange, IPaginationRecordset } from "libraries/component/pagination/IPagination";
import { ITicketByTraveller } from "./components/booking-traveller/components/booking-traveller-ticket/ITicketByTraveller";
import { CardComponent } from "libraries/ui_card/card.component";
import { IGetEnumsService } from 'libraries/service/booking/enums/IGetEnums';
import { FormGroup } from "@angular/forms";
import { BookingResourceService } from "libraries/service/resources/booking/bookingResource.service";
import { finalize } from "rxjs/operators";
import { NotificationType } from "libraries/interfaces/INotification";
import { BookingTravellerComponent } from "libraries/component/booking-information/components/booking-traveller-card/components/booking-traveller/booking-traveller.component";
import { markAllTouched } from "libraries/service/form/mark-touched.util";

type Booking = IGetBookingsAirResponse | IGetBookingsStayResponse | IGetBookingsVehicleRentalResponse;

@Component({
    selector: 'booking-traveller-card',
    template: `
        <card #cardContainer [isCollapsible]="true" class="collapsible">
            <div class="card-header-content">
                <div class="title" (click)="menuToggleCard()">
                    {{ cardLabels.title }}
                </div>

                <div *ngIf="cardContainer.showContent" class="tabs-field">
                    <menu-details [items]="menuItems" (onChangeTab)="changeTab($event)"></menu-details>
                </div>
                <div class="editButton" *ngIf="isWithFormButtons && tab === 'travellers' && !loading && !error">
                    <btn type="actionHeaderLink"
                         (click)="onCancel()"
                         [label]="editLabel.cancel">
                    </btn>
                    <btn type="actionPrincipal"
                         (click)="updateIdentityInfos()"
                         [label]="editLabel.validate">
                    </btn>
                </div>
            </div>

            <div class="card-body-content">
                <div class="booking-data-content">
                    <loader *ngIf="loading" [message]="wording.applyingUpdate"></loader>
                    <notification *ngIf="error" [type]="errorNotificationType" [showCloseButton]="true" (onClose)="error = null">
                        {{error.message}}
                    </notification>
                    <ng-container *ngIf="!error && !loading && menuItems.length && travellerPages.length">
                        <booking-traveller #bookingTravellerComponent
                                           *ngFor="let traveller of travellerPages[page]; let i = index; trackBy: trackTravellerByXId"
                                           class="e2e-booking-traveller-{{i}}"
                                           [host]="bookingData.host"
                                           [traveller]="traveller"
                                           [view]="tab"
                                           [ageDate]="ageDate"
                                           [ticket]="ticketByTraveller[traveller.xId]"
                                           [sector]="sector"
                                           [travellerFormGroup]="travellersFormGroup.controls[traveller.xId]"
                                           [isEditable]="isEditable"
                                           (onFormPristineChange)="updateFormButtonsVisibility()">
                        </booking-traveller>
                    </ng-container>
                </div>
                <pagination [listLength]="maxRows"
                            [recordset]="recordset"
                            [pageSizeOptions]="pageSizeOptions"
                            (onPageChange)="changePage($event)" *ngIf="travellerPages.length > 1"></pagination>
            </div>
        </card>
    `,
    styleUrls: ['../card.scss'],
    styles: [`
        booking-traveller {
            display: block;
        }

        booking-traveller:nth-child(even) {
            background: #f2f2f2;
        }

        booking-traveller:nth-child(odd) {
            background: #f9fafc;
        }
    `]
})
export class BookingTravellerCardComponent implements OnChanges {

    private static readonly TRAVELLERS_HASH: string = 'travellers';

    @ViewChildren('bookingTravellerComponent')
    public bookingTravellerComponents: QueryList<BookingTravellerComponent>;

    @Input()
    public travellers: ITraveller[];
    public travellerPages: ITraveller[][];
    public page: number = 0;

    @Input()
    public ageDate: string;

    @Input()
    public ticket: ITicket[];

    @Input()
    public sector: Array<ISectorAir | ISectorStay>;

    @Input()
    public bookingData: Booking;

    @Input()
    public serviceEnum: IGetEnumsService[];

    @Input()
    public isEditable: boolean;

    @ViewChild('cardContainer')
    public cardContainer: CardComponent;

    @Output()
    public readonly onCardTitleClick: EventEmitter<string> = new EventEmitter();

    @Output()
    public readonly onBookingRefreshed: EventEmitter<TGetBookingResponse> = new EventEmitter();

    public readonly cardName: string = 'bookingTraveller';

    public readonly cardLabels: ILabels = {
        title: Yii.t('wui-shared-js', 'Voyageurs'),
        hideDetails: Yii.t('wui-shared-js', 'Cacher les détails'),
        showDetails: Yii.t('wui-shared-js', 'Montrer les détails'),
    };

    public readonly tabLabels: ILabels = {
        details: Yii.t('wui-shared-js', 'Détails'),
        contact: Yii.t('wui-shared-js', 'Contact'),
        ticket: Yii.t('wui-shared-js', 'Billets'),
    };

    public readonly editLabel: { cancel: string, validate: string } = {
        cancel: Yii.t('wui-shared-js', 'Abandonner'),
        validate: Yii.t('wui-shared-js', 'Valider')
    };

    public readonly wording: ILabels = {
        applyingUpdate: Yii.t('wui-shared-js', 'Application des modifications...'),
        fillRequiredFields: Yii.t('wui-shared-js', "Veuiller remplir tous les champs..."),
    };
    public readonly errorNotificationType: NotificationType = NotificationType.Error;

    public tab: string = 'travellers';

    public readonly menuItems: IMenuDetails[] = [
        {
            code: 'travellers',
            name: this.tabLabels.details,
            exist: true,
            selected: true
        },
        {
            code: 'contacts',
            name: this.tabLabels.contact,
            exist: true,
            selected: false
        },
        {
            code: 'tickets',
            name: this.tabLabels.ticket,
            exist: true,
            selected: false
        },
    ];

    public recordset: IPaginationRecordset;

    public maxRows: number = 10;

    public pageSizeOptions: number[] = null;

    public ticketByTraveller: ITicketByTraveller[] = [];

    public travellersFormGroup: FormGroup;

    public loading: boolean = false;

    public error: Error = null;

    public isWithFormButtons: boolean = false;

    constructor(
        private readonly menuDetailsService: MenuDetailsService,
        private readonly bookingResourceService: BookingResourceService,
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {

        if (changes.travellers && changes.travellers.currentValue) {
            const travellers: ITraveller[] = changes.travellers.currentValue;

            travellers.sort((a: ITraveller, b: ITraveller) => {
                if (a.xId < b.xId) {
                    return -1;
                }
                if (a.xId > b.xId) {
                    return 1;
                }
                return 0;
            });
            if (this.ticket) {
                travellers.forEach((traveller: ITraveller) => {
                    this.ticketByTraveller[traveller.xId] = this.ticket.filter(
                        (ticketData: ITicket) => ticketData.traveller.xRef === traveller.xId
                    );
                });
            }
            this.travellerPages = chunk(travellers, this.maxRows);

            this.recordset = {
                total: this.travellers.length,
                first: 1,
            };
        }
        this.initForm();
    }

    public trackTravellerByXId(index: number, item: ITraveller): string {
        return item.xId;
    }

    public changeTab(tabCode: string): void {
        this.menuDetailsService.changeTab(this.menuItems, tabCode);
        this.tab = tabCode;
    }

    public changePage($event: IPageChange): void {
        this.page = $event.page;
    }

    public menuToggleCard(): void {
        this.onCardTitleClick.emit(this.cardName);
    }

    public initForm(): void {
        this.travellersFormGroup = new FormGroup({});
        this.travellers.forEach((traveller: ITraveller) => {
            this.travellersFormGroup.addControl(traveller.xId, new FormGroup({}));
        });
    }

    public updateIdentityInfos(): void {
        markAllTouched(this.travellersFormGroup);
        if (!this.travellersFormGroup.valid) {
            this.bookingTravellerComponents.forEach((item: BookingTravellerComponent) => {
                item.onParentFormCheck();
            });
            return;
        }

        const travellersToUpdate: ITraveller[] = this.bookingTravellerComponents.map((item: BookingTravellerComponent) => item.getTraveller());

        this.loading = true;
        this.bookingResourceService.updateTravellerIdentity(this.bookingData.syscode, travellersToUpdate).pipe(
            finalize(() => {
                this.loading = false;
                window.location.hash = BookingTravellerCardComponent.TRAVELLERS_HASH;
            })
        ).subscribe(
            (value: IGetBookingsAirResponse) => {
                this.onBookingRefreshed.emit(value);
            },
            (error: Error) => this.error = error
        );
    }

    public onCancel(): void {
        this.bookingTravellerComponents.forEach((item: BookingTravellerComponent) => {
            item.onParentCancel();
        });
        this.updateFormButtonsVisibility();
    }

    public updateFormButtonsVisibility(): void {
        this.isWithFormButtons = !this.travellersFormGroup.pristine;
    }

}

function chunk(travellers: ITraveller[], size: number): ITraveller[][] {
    const chunked_arr: ITraveller[][] = [];
    let index: number = 0;
    while (index < travellers.length) {
        chunked_arr.push(travellers.slice(index, size + index));
        index += size;
    }
    return chunked_arr;
}
